import { STORAGE } from '../constants'
import type { HdrMode } from '../types'

export type HdrSupport = 'supported' | 'unsupported' | 'unknown'
export type HdrDisplay = 'high' | 'standard' | 'unknown'
export type HdrFormat = '' | 'HDR10' | 'HDR10+' | 'Dolby Vision'

/** A policy rejection must outweigh every resolution, codec, peer and bitrate
 * bonus. HDR mode is a viewer contract, not a ranking suggestion. */
export const HDR_POLICY_BLOCK = -10_000

export interface HdrCapabilities {
  display: HdrDisplay
  hdr10: HdrSupport
  hdr10Plus: HdrSupport
  dolbyVision: HdrSupport
  source: string
  testedAt: number
}

export const UNKNOWN_HDR_CAPABILITIES: HdrCapabilities = {
  display: 'unknown',
  hdr10: 'unknown',
  hdr10Plus: 'unknown',
  dolbyVision: 'unknown',
  source: 'not probed',
  testedAt: 0,
}

interface MediaCapabilitiesLike {
  decodingInfo(configuration: Record<string, unknown>): Promise<{ supported?: boolean; smooth?: boolean; powerEfficient?: boolean }>
}

function supportFromMedia(value: CanPlayTypeResult): HdrSupport {
  return value === 'probably' || value === 'maybe' ? 'supported' : 'unsupported'
}

function displayCapability(): HdrDisplay {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'unknown'
  if (window.matchMedia('(dynamic-range: high)').matches) return 'high'
  if (window.matchMedia('(dynamic-range: standard)').matches) return 'standard'
  return 'unknown'
}

export function combineDecodeAndDisplay(decode: HdrSupport, display: HdrDisplay): HdrSupport {
  if (decode === 'unsupported') return 'unsupported'
  if (decode === 'supported' && display === 'high') return 'supported'
  // webOS/Chrome can report `standard` when the media query is incomplete or
  // before an HDR video activates the output path. It is not proof that the
  // physical panel is SDR, so keep it unknown and let Prefer HDR opt in.
  return 'unknown'
}

export async function detectHdrCapabilities(): Promise<HdrCapabilities> {
  if (typeof document === 'undefined') return UNKNOWN_HDR_CAPABILITIES
  const video = document.createElement('video')
  const display = displayCapability()
  const hevc10 = supportFromMedia(video.canPlayType('video/mp4; codecs="hvc1.2.4.L153.B0"'))
  const dv = supportFromMedia(
    video.canPlayType('video/mp4; codecs="dvh1.05.06"')
      || video.canPlayType('video/mp4; codecs="dvhe.05.06"'),
  )
  let hdr10Decode = hevc10
  const mediaCapabilities = (navigator as Navigator & { mediaCapabilities?: MediaCapabilitiesLike }).mediaCapabilities
  if (mediaCapabilities) {
    try {
      const result = await mediaCapabilities.decodingInfo({
        type: 'file',
        video: {
          contentType: 'video/mp4; codecs="hvc1.2.4.L153.B0"',
          width: 3840,
          height: 2160,
          bitrate: 20_000_000,
          framerate: 30,
          colorGamut: 'rec2020',
          transferFunction: 'pq',
        },
      })
      if (result.supported === true) hdr10Decode = 'supported'
      if (result.supported === false) hdr10Decode = 'unsupported'
    } catch { /* canPlayType remains the conservative signal */ }
  }

  const capabilities: HdrCapabilities = {
    display,
    hdr10: combineDecodeAndDisplay(hdr10Decode, display),
    // HDR10+ uses the same HEVC/PQ base, but the browser exposes no reliable
    // dynamic-metadata probe. Never claim support from HDR10 alone.
    hdr10Plus: hdr10Decode === 'unsupported' ? 'unsupported' : 'unknown',
    dolbyVision: combineDecodeAndDisplay(dv, display),
    source: mediaCapabilities ? 'display query + MediaCapabilities + canPlayType' : 'display query + canPlayType',
    testedAt: Date.now(),
  }
  try { localStorage.setItem(STORAGE.hdrCapabilities, JSON.stringify(capabilities)) } catch { /* private mode */ }
  return capabilities
}

export function getStoredHdrCapabilities(): HdrCapabilities {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE.hdrCapabilities) || 'null') as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return UNKNOWN_HDR_CAPABILITIES
    const raw = value as Record<string, unknown>
    if (!['high', 'standard', 'unknown'].includes(raw.display as string)) return UNKNOWN_HDR_CAPABILITIES
    const support = (entry: unknown): HdrSupport => ['supported', 'unsupported', 'unknown'].includes(entry as string)
      ? entry as HdrSupport : 'unknown'
    return {
      display: raw.display as HdrDisplay,
      hdr10: support(raw.hdr10),
      hdr10Plus: support(raw.hdr10Plus),
      dolbyVision: support(raw.dolbyVision),
      source: typeof raw.source === 'string' && raw.source.trim() ? raw.source.trim().slice(0, 200) : 'not probed',
      testedAt: typeof raw.testedAt === 'number' && Number.isFinite(raw.testedAt) && raw.testedAt > 0
        ? Math.min(raw.testedAt, 9_999_999_999_999) : 0,
    }
  } catch {
    return UNKNOWN_HDR_CAPABILITIES
  }
}

export function hdrSupport(format: HdrFormat, capabilities: HdrCapabilities): HdrSupport {
  if (!format) return 'supported'
  if (format === 'HDR10') return capabilities.hdr10
  if (format === 'HDR10+') return capabilities.hdr10Plus
  return capabilities.dolbyVision
}

export function scoreHdr(format: HdrFormat, capabilities: HdrCapabilities, mode: HdrMode) {
  if (!format) return { score: mode === 'prefer' ? -8 : 0, risk: '', policyBlocked: false }
  if (mode === 'sdr') return { score: HDR_POLICY_BLOCK, risk: 'HDR disabled in Settings', policyBlocked: true }
  const support = hdrSupport(format, capabilities)
  if (support === 'unsupported') return { score: HDR_POLICY_BLOCK, risk: `${format} is not supported by this display`, policyBlocked: true }
  if (support === 'unknown') {
    return mode === 'prefer'
      ? { score: 10, risk: `${format} support is not confirmed`, policyBlocked: false }
      : { score: HDR_POLICY_BLOCK, risk: `${format} support is not confirmed; Auto requires a confirmed HDR path`, policyBlocked: true }
  }
  return { score: mode === 'prefer' ? 28 : 18, risk: '', policyBlocked: false }
}

export function hdrStatusNote(format: HdrFormat, capabilities: HdrCapabilities, playing: boolean) {
  if (!format) return 'SDR source'
  const support = hdrSupport(format, capabilities)
  if (support === 'unsupported') return `${format} source; TV reports no compatible HDR path`
  if (support === 'unknown') return `${format} source; confirm the panel HDR badge during the live test`
  return playing
    ? `${format} source playing; panel badge is the final output confirmation`
    : `${format} source supported; waiting for playback`
}
